import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';

import { Announcement, AnnouncementType, ReorderAnnouncementData } from '@breathelife/types';
import { Box, IconButton, Tooltip } from '@breathelife/mui';

import { MuiVisibilityIcon } from '../../../Assets/Icons/MuiVisibilityIcon';
import { MuiVisibilityOffIcon } from '../../../Assets/Icons/MuiVisibilityOffIcon';
import Typography from '../../../Components/Typography';
import { getLanguage } from '../../../Localization/utils';
import styled from '../../../Styles/themed-styled-components';
import { emptyTableHeight } from '../../../Models/Layout';

const EmptyContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: ${() => `calc(100vh - ${emptyTableHeight}px)`};
  min-height: 300px;
`;

const TableContainer = styled.div`
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.grey[30]};
  border-radius: 4px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  background-color: ${(props) => props.theme.colors.grey[20]};
  padding: 12px 16px;
  font-weight: 600;
`;

const HeaderCell = styled.div<{ $flex?: number }>`
  flex: ${(props) => props.$flex || 1};
  padding: 0 8px;
`;

const TableRow = styled.div<{ $isDragging?: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[20]};
  background-color: ${(props) => (props.$isDragging ? props.theme.colors.grey[10] : 'white')};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.grey[10]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div<{ $flex?: number }>`
  flex: ${(props) => props.$flex || 1};
  padding: 0 8px;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  padding-right: 8px;
  color: ${(props) => props.theme.colors.grey[50]};

  &:active {
    cursor: grabbing;
  }
`;

type Props = {
  announcements: Announcement[];
  onRowClick: (id?: string) => void;
  onReorder?: (data: ReorderAnnouncementData[]) => void;
  onTogglePublish?: (id: string, published: boolean) => void;
};

export function AnnouncementsTable({ announcements, onRowClick, onReorder, onTogglePublish }: Props): ReactElement {
  const { t } = useTranslation();

  const getTypeLabel = useCallback(
    (type: AnnouncementType): string => {
      switch (type) {
        case AnnouncementType.info:
          return t('admin.announcementsManagement.type.info');
        case AnnouncementType.warning:
          return t('admin.announcementsManagement.type.warning');
        case AnnouncementType.success:
          return t('admin.announcementsManagement.type.success');
        case AnnouncementType.error:
          return t('admin.announcementsManagement.type.error');
        default:
          return type;
      }
    },
    [t],
  );

  const handleDragEnd = useCallback(
    (result: DropResult): void => {
      if (!result.destination || !onReorder) {
        return;
      }

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      if (sourceIndex === destinationIndex) {
        return;
      }

      const reorderedAnnouncements = Array.from(announcements);
      const [movedItem] = reorderedAnnouncements.splice(sourceIndex, 1);
      reorderedAnnouncements.splice(destinationIndex, 0, movedItem);

      const reorderData: ReorderAnnouncementData[] = reorderedAnnouncements.map((announcement, index) => ({
        id: announcement.id,
        displayOrder: index + 1,
      }));

      onReorder(reorderData);
    },
    [announcements, onReorder],
  );

  const isAnnouncementsListEmpty = announcements.length === 0;

  if (isAnnouncementsListEmpty) {
    return (
      <EmptyContainer>
        <Box mb={1.5}>
          <Typography variant='h2' grey={70}>
            {t('admin.announcementsManagement.noAnnouncementsFoundTitle')}
          </Typography>
        </Box>
        <Box maxWidth={300}>
          <Typography variant='body1' grey={60}>
            {t('admin.announcementsManagement.noAnnouncementsFoundText')}
          </Typography>
        </Box>
      </EmptyContainer>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <TableContainer>
        <TableHeader>
          <HeaderCell $flex={0.5}></HeaderCell>
          <HeaderCell $flex={3}>{t('admin.announcementsManagement.labels.title')}</HeaderCell>
          <HeaderCell $flex={1}>{t('admin.announcementsManagement.labels.type')}</HeaderCell>
          <HeaderCell $flex={1}>{t('admin.announcementsManagement.labels.actions')}</HeaderCell>
        </TableHeader>
        <Droppable droppableId='announcements'>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {announcements.map((announcement, index) => (
                <Draggable key={announcement.id} draggableId={announcement.id} index={index}>
                  {(provided, snapshot) => (
                    <TableRow
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      $isDragging={snapshot.isDragging}
                      onClick={() => onRowClick(announcement.id)}
                    >
                      <TableCell $flex={0.5}>
                        <DragHandle {...provided.dragHandleProps}>⋮⋮</DragHandle>
                      </TableCell>
                      <TableCell $flex={3}>
                        {announcement.title[getLanguage()] || announcement.title.en || ''}
                      </TableCell>
                      <TableCell $flex={1}>{getTypeLabel(announcement.type)}</TableCell>
                      <TableCell $flex={1}>
                        <Tooltip
                          title={
                            announcement.published
                              ? t('admin.announcementsManagement.actions.unpublish')
                              : t('admin.announcementsManagement.actions.publish')
                          }
                        >
                          <IconButton
                            sx={{ width: 40, height: 40 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTogglePublish?.(announcement.id, !announcement.published);
                            }}
                          >
                            {announcement.published ? (
                              <MuiVisibilityIcon svgProps={{ fill: '#1976d2' }} />
                            ) : (
                              <MuiVisibilityOffIcon svgProps={{ fill: '#757575' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </TableContainer>
    </DragDropContext>
  );
}
